import { useCallback, useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ConfirmationModal from 'components/ConfirmationModal'
import { INPUT_MASK } from 'constants/masks'
import { useGlobalState } from 'contexts/global-state'
import moment from 'moment'
import { ApiService } from 'services/api'
import { CacheService } from 'services/cache'

const DATE_START_ISSUE_INVOICE = moment('2025-04-01')

type UpdateInvoiceStatusButtonProps = {
  activity: Activity
  type: 'icon' | 'button'
  onLoading?: (value: boolean) => void
}

type UpdateInvoiceStatusModalProps = {
  opened: boolean
  activity_name: string
  onClose: () => void
  onConfim: () => void
  onCancel: () => void
}

const UpdateInvoiceStatusModal = ({
  onCancel,
  onClose,
  onConfim,
  opened,
  activity_name,
}: UpdateInvoiceStatusModalProps) => {
  return (
    <ConfirmationModal
      title="Emissão de nota fiscal"
      opened={opened}
      onClose={onClose}
      onConfim={onConfim}
      onCancel={onCancel}
    >
      <p>
        Deseja confirmar a emissão de nota fiscal para a atividade{' '}
        <strong>{activity_name}</strong>
      </p>
    </ConfirmationModal>
  )
}

export const UpdateInvoiceStatusButton = ({
  activity,
  onLoading,
}: UpdateInvoiceStatusButtonProps) => {
  const { openInfoToast, openErrorToast, openSuccessToast } = useGlobalState()

  const [updateInvoiceStatusModal, setUpdateInvoiceStatusModal] =
    useState(false)

  const handleUpdateStatusInvoice = useCallback(async () => {
    onLoading?.(true)
    const { id } = activity
    try {
      const responseUpdateStatusInvoice =
        await ApiService.Activities.updateInvoiceStatus({
          id: id.toString(),
        })
      CacheService.queryClient.invalidateQueries()
      if (!responseUpdateStatusInvoice.success) {
        setUpdateInvoiceStatusModal(false)
        openInfoToast({
          message: responseUpdateStatusInvoice.message,
        })
        onLoading?.(false)
        return
      }
      setUpdateInvoiceStatusModal(false)
      openSuccessToast({
        message: 'Processo de emissão de nota fiscal iniciado com sucesso',
      })
      onLoading?.(false)
    } catch (error: unknown) {
      openErrorToast({
        message: (error as ApiError)?.message || (error as string),
      })
    }
  }, [activity, onLoading, openErrorToast, openInfoToast, openSuccessToast])

  const isDisabled = useMemo(() => {
    if (activity.status !== 'finished') {
      return true
    }

    if (activity.invoice_status) {
      return true
    }
  }, [activity.invoice_status, activity.status])

  if (
    moment(activity.start_travel_date, INPUT_MASK.DATE_LOCALE).isBefore(
      moment(DATE_START_ISSUE_INVOICE),
    )
  ) {
    return null
  }

  return (
    <>
      <Box sx={{ m: 1 }}>
        <Button
          color="info"
          variant="contained"
          onClick={() => {
            setUpdateInvoiceStatusModal(true)
          }}
          disabled={isDisabled}
        >
          Emissão de nota fiscal
        </Button>
      </Box>

      <UpdateInvoiceStatusModal
        activity_name={activity.name}
        onCancel={() => setUpdateInvoiceStatusModal(false)}
        onClose={() => setUpdateInvoiceStatusModal(false)}
        onConfim={handleUpdateStatusInvoice}
        opened={updateInvoiceStatusModal}
      />
    </>
  )
}
